import React, { useState, useEffect } from "react";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { API_BASE } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, ArrowRight, Layers, TrendingUp, ChevronDown, ChevronRight,
  Target, Compass, PenLine, Calendar, BookOpen, FileText, Search, X
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from "recharts";

// per-paper accent theme
const THEMES = {
  GS1: { grad: "from-orange-500 to-amber-500", soft: "from-orange-50 to-amber-50", border: "border-orange-200", text: "text-orange-600", chip: "bg-orange-100 text-orange-700", bar: "#f97316", pill: "bg-orange-500" },
  GS2: { grad: "from-blue-500 to-indigo-500", soft: "from-blue-50 to-indigo-50", border: "border-blue-200", text: "text-blue-600", chip: "bg-blue-100 text-blue-700", bar: "#3b82f6", pill: "bg-blue-500" },
  GS3: { grad: "from-emerald-500 to-teal-500", soft: "from-emerald-50 to-teal-50", border: "border-emerald-200", text: "text-emerald-600", chip: "bg-emerald-100 text-emerald-700", bar: "#10b981", pill: "bg-emerald-500" },
  GS4: { grad: "from-purple-500 to-fuchsia-500", soft: "from-purple-50 to-fuchsia-50", border: "border-purple-200", text: "text-purple-600", chip: "bg-purple-100 text-purple-700", bar: "#a855f7", pill: "bg-purple-500" },
};

// ── formatters to break "1) … 2) …" titles and "(a)(b)(c)" sub-questions ──
const splitNumbered = (s) => {
  if (!s) return [];
  return s.split(/(?=\d\)\s)/).map((p) => p.trim()).filter(Boolean);
};

const NumberedTitle = ({ text }) => {
  const parts = splitNumbered(text);
  if (parts.length <= 1) return <>{text}</>;
  return (
    <ol className="list-none space-y-1">
      {parts.map((p, i) => {
        const m = p.match(/^(\d)\)\s*([\s\S]*)$/);
        return (
          <li key={i} className="flex gap-1.5">
            <span className="opacity-60">{m ? m[1] : i + 1}.</span>
            <span>{m ? m[2] : p}</span>
          </li>
        );
      })}
    </ol>
  );
};

const splitQuestion = (s) => {
  if (!s) return { scenario: "", parts: [] };
  const idx = s.search(/\([a-d]\)\s/);
  if (idx === -1) return { scenario: s, parts: [] };
  const scenario = s.slice(0, idx).trim();
  const parts = s.slice(idx).split(/(?=\([a-d]\)\s)/).map((p) => p.trim()).filter(Boolean);
  return { scenario, parts };
};

const QuestionText = ({ text, accent }) => {
  const { scenario, parts } = splitQuestion(text);
  if (!parts.length) return <p className="text-sm font-medium text-slate-800 leading-relaxed">{text}</p>;
  return (
    <div className="space-y-2">
      {scenario && <p className="text-sm text-slate-700 leading-relaxed">{scenario}</p>}
      <ul className="space-y-1.5">
        {parts.map((p, i) => {
          const m = p.match(/^\(([a-d])\)\s*([\s\S]*)$/);
          return (
            <li key={i} className="flex gap-2">
              <span className={`font-bold ${accent} flex-shrink-0`}>({m ? m[1] : i})</span>
              <span className="text-sm font-medium text-slate-800 leading-relaxed">{m ? m[2] : p}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const GSPYQBank = ({ paper = "GS1", onPractice, onBack }) => {
  const t = THEMES[paper] || THEMES.GS1;
  const [view, setView] = useState("overview"); // overview | chapter
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [chapterDetail, setChapterDetail] = useState(null);
  const [chapterLoading, setChapterLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // pyq_id -> bool
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults(null); setSearching(false); return; }
    setSearching(true);
    const id = setTimeout(async () => {
      try {
        const { data } = await managedAxios.get(`${API_BASE}/gs-pyq/records?paper=${paper}&q=${encodeURIComponent(q)}`);
        setResults(data.records || []);
      } catch (e) {
        if (!isCancelError(e)) console.error(e);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(id);
  }, [query, paper]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await managedAxios.get(`${API_BASE}/gs-pyq/overview?paper=${paper}`);
        if (alive) setOverview(data);
      } catch (e) {
        if (!isCancelError(e)) console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [paper]);

  const openChapter = async (order) => {
    setChapterLoading(true);
    setView("chapter");
    setExpanded({});
    window.scrollTo({ top: 0, behavior: "smooth" });
    try {
      const { data } = await managedAxios.get(`${API_BASE}/gs-pyq/chapter/${paper}/${order}`);
      setChapterDetail(data);
    } catch (e) {
      if (!isCancelError(e)) console.error(e);
    } finally {
      setChapterLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-400" data-testid="gs-pyq-loading">
        <div className="animate-pulse text-sm">Loading {paper} PYQ analysis…</div>
      </div>
    );
  }

  // ─────────────────────────── CHAPTER DETAIL VIEW ───────────────────────────
  if (view === "chapter") {
    const c = chapterDetail?.chapter;
    const subtopics = chapterDetail?.subtopics || [];
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="gs-pyq-chapter-view">
        <button
          onClick={() => { setView("overview"); setChapterDetail(null); }}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800"
          data-testid="gs-pyq-chapter-back"
        >
          <ArrowLeft className="w-4 h-4" /> All chapters
        </button>

        {chapterLoading || !c ? (
          <div className="py-16 text-center text-slate-400 animate-pulse">Loading chapter…</div>
        ) : (
          <>
            <Card className={`border ${t.border} bg-gradient-to-br ${t.soft} overflow-hidden`}>
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${t.grad} flex items-center justify-center shadow-md flex-shrink-0`}>
                    <BookOpen className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{c.slot}</p>
                    <h1 className="font-heading text-xl font-bold text-slate-900 mt-1 leading-snug"><NumberedTitle text={c.chapter_syllabus} /></h1>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <Badge className={`${t.chip} border-0 text-xs`}>{c.domain}</Badge>
                      <Badge className="bg-white/70 text-slate-700 border-0 text-xs">{c.pyq_count} PYQs</Badge>
                      <Badge className="bg-white/70 text-slate-700 border-0 text-xs">{c.marks_total} marks</Badge>
                    </div>
                  </div>
                </div>
                {c.years && (
                  <p className="text-xs text-slate-500 mt-4">
                    <Calendar className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Appeared in: {c.years}
                  </p>
                )}
              </CardContent>
            </Card>

            <div className="space-y-4">
              {subtopics.map((st, si) => (
                <div key={si} data-testid={`gs-pyq-subtopic-${si}`}>
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <Layers className={`w-4 h-4 ${t.text}`} />
                    <h2 className="text-sm md:text-base font-heading font-bold text-slate-800">{st.subtopic}</h2>
                    <span className="text-[11px] text-slate-400">· {st.records.length} PYQ{st.records.length > 1 ? "s" : ""}</span>
                  </div>
                  <div className="space-y-3">
                    {st.records.map((r) => (
                      <RecordCard
                        key={r.pyq_id}
                        r={r}
                        t={t}
                        open={!!expanded[r.pyq_id]}
                        onToggle={() => setExpanded((s) => ({ ...s, [r.pyq_id]: !s[r.pyq_id] }))}
                        onPractice={onPractice}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // ─────────────────────────── OVERVIEW VIEW ───────────────────────────
  const trend = overview?.year_breakdown || [];
  const slots = overview?.slots || [];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="gs-pyq-overview">
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800" data-testid="gs-pyq-back-home">
          <ArrowLeft className="w-4 h-4" /> {paper} Suite
        </button>
      )}

      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
          <FileText className={`w-6 h-6 ${t.text}`} /> {paper} PYQ Analysis
        </h1>
        <p className="text-sm text-slate-500 mt-1">{overview?.total_pyqs} questions · {overview?.total_chapters} chapters · {overview?.coverage}</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard t={t} value={overview?.total_pyqs} label="Total PYQs" />
        <StatCard t={t} value={overview?.total_marks} label="Total Marks" />
        <StatCard t={t} value={overview?.total_chapters} label="Chapters" />
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${paper} questions by keyword…`}
          className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
          data-testid="gs-pyq-search-input"
        />
        {query && (
          <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="gs-pyq-search-clear">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {results !== null ? (
        <div className="space-y-3" data-testid="gs-pyq-search-results">
          <p className="text-xs text-slate-500">{searching ? "Searching…" : `${results.length} result${results.length !== 1 ? "s" : ""} for "${query.trim()}"`}</p>
          {results.map((r) => (
            <RecordCard
              key={r.pyq_id}
              r={r}
              t={t}
              open={!!expanded[r.pyq_id]}
              onToggle={() => setExpanded((s) => ({ ...s, [r.pyq_id]: !s[r.pyq_id] }))}
              onPractice={onPractice}
              context={`${r.chapter_syllabus} · ${r.subtopic}`}
            />
          ))}
          {!searching && results.length === 0 && (
            <p className="text-sm text-slate-400 py-10 text-center">No questions match "{query.trim()}". Try another keyword.</p>
          )}
        </div>
      ) : (
      <>
      {/* Trend chart */}
      <Card className="border border-slate-200 bg-white">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className={`w-4 h-4 ${t.text}`} />
            <p className="font-heading font-bold text-slate-800 text-sm">Year-wise question count ({overview?.year_span})</p>
          </div>
          <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer>
              <BarChart data={trend} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="year" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Bar dataKey="count" name="PYQs" fill={t.bar} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Slots -> chapters */}
      <div className="space-y-6">
        {slots.map((slot, si) => (
          <div key={si} data-testid={`gs-pyq-slot-${si}`}>
            <div className="flex items-center gap-2 mb-3 px-1">
              <div className={`h-6 w-1 rounded-full bg-gradient-to-b ${t.grad}`} />
              <h2 className="text-sm font-heading font-bold text-slate-700 uppercase tracking-wide">{slot.slot}</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {slot.chapters.map((c) => (
                <Card
                  key={c.chapter_order}
                  style={{ borderLeftColor: t.bar, borderLeftWidth: "4px" }}
                  className={`border ${t.border} bg-white hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer group`}
                  onClick={() => openChapter(c.chapter_order)}
                  data-testid={`gs-pyq-chapter-card-${c.chapter_order}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={`${t.chip} border-0 text-[10px]`}>{c.pyq_count} PYQs</Badge>
                      <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px]">{c.marks_total}m</Badge>
                    </div>
                    <h3 className={`font-heading font-bold text-slate-800 text-sm leading-snug group-hover:${t.text} transition-colors`}><NumberedTitle text={c.chapter_syllabus} /></h3>
                    <p className="text-xs text-slate-500 mt-1">{c.domain}</p>
                    <div className="flex items-center justify-end mt-3">
                      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:translate-x-0.5 transition-all" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
      </>
      )}
    </div>
  );
};

const RecordCard = ({ r, t, open, onToggle, onPractice, context }) => (
  <Card className="border border-slate-200 bg-white overflow-hidden" data-testid={`gs-pyq-record-${r.pyq_id}`}>
    <button
      className="w-full text-left p-4 flex items-start gap-3 hover:bg-slate-50 transition-colors"
      onClick={onToggle}
      data-testid={`gs-pyq-record-toggle-${r.pyq_id}`}
    >
      <div className="flex flex-col items-center gap-1 flex-shrink-0 w-12">
        <span className={`text-xs font-mono font-bold ${t.text}`}>{r.year}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{r.marks}m</span>
      </div>
      <div className="flex-1 min-w-0">
        {context && <p className="text-[11px] text-slate-400 mb-1 truncate">{context}</p>}
        {open
          ? <QuestionText text={r.question} accent={t.text} />
          : <p className="text-sm font-medium text-slate-800 leading-relaxed line-clamp-2">{r.question}</p>}
      </div>
      {open ? <ChevronDown className="w-4 h-4 text-slate-400 mt-1" /> : <ChevronRight className="w-4 h-4 text-slate-400 mt-1" />}
    </button>
    {open && (
      <div className="px-4 pb-4 pt-0 space-y-3 border-t border-slate-100">
        <div className="flex flex-wrap gap-2 pt-3">
          {r.question_demand && <span className={`text-xs px-2.5 py-1 rounded-full ${t.chip}`}>Demand: {r.question_demand}</span>}
          {(r.dimensions || "").split("|").map((d, i) => d.trim() && (
            <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">{d.trim()}</span>
          ))}
        </div>
        {r.what_upsc_is_testing && (
          <Info icon={Target} color="text-rose-600" bg="bg-rose-50" label="What UPSC is testing" text={r.what_upsc_is_testing} />
        )}
        {r.clarias_answer_approach && (
          <Info icon={Compass} color="text-amber-600" bg="bg-amber-50" label="Answer approach" text={r.clarias_answer_approach} />
        )}
        <Button
          className={`w-full mt-1 bg-gradient-to-r ${t.grad} text-white`}
          onClick={() => onPractice && onPractice(r.question, r.marks)}
          data-testid={`gs-pyq-practice-${r.pyq_id}`}
        >
          <PenLine className="w-4 h-4 mr-2" /> Practice this question
        </Button>
      </div>
    )}
  </Card>
);

const StatCard = ({ t, value, label }) => (
  <Card className={`border ${t.border} bg-gradient-to-br ${t.soft}`}>
    <CardContent className="p-4 text-center">
      <p className={`text-3xl sm:text-4xl font-bold font-heading ${t.text}`}>{value ?? "—"}</p>
      <p className="text-[11px] text-slate-500 mt-0.5 uppercase tracking-wide">{label}</p>
    </CardContent>
  </Card>
);

const Info = ({ icon: Icon, color, bg, label, text }) => (
  <div className={`${bg} rounded-lg p-3`}>
    <p className={`text-[11px] font-semibold uppercase tracking-wide ${color} mb-1 flex items-center gap-1`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </p>
    <p className="text-sm text-slate-700">{text}</p>
  </div>
);

export default GSPYQBank;
